/**
 * Avatar — Unified user avatar with hash-based gradient colors (#428)
 *
 * Replaces all `owner_user_id?.[0]?.toUpperCase()` + fixed blue bg
 * with userId-hashed HSL so each user has a distinct color.
 *
 * Usage:
 *   <Avatar userId="abc-123" />
 *   <Avatar userId="abc-123" name="Alice" size="lg" />
 */

const SIZE_MAP = {
  xs: "w-5 h-5 text-xs",
  sm: "w-6 h-6 text-xs",
  md: "w-8 h-8 text-sm",
  lg: "w-12 h-12 text-base",
  xl: "w-16 h-16 text-xl",
} as const;

export type AvatarSize = keyof typeof SIZE_MAP;

interface AvatarProps {
  userId: string;
  name?: string | null;
  avatarUrl?: string | null;
  size?: AvatarSize;
  className?: string;
}

/** Simple string hash → 0..359 hue */
function hashToHue(str: string): number {
  let h = 0;
  for (let i = 0; i < str.length; i++) {
    h = ((h << 5) - h + str.charCodeAt(i)) | 0;
  }
  return ((h % 360) + 360) % 360;
}

export function Avatar({ userId, name, avatarUrl, size = "md", className = "" }: AvatarProps) {
  // If there's a real avatar URL, use it
  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={name ?? userId.slice(0, 6)}
        className={`${SIZE_MAP[size]} rounded-full object-cover flex-shrink-0 ${className}`}
      />
    );
  }

  const hue = hashToHue(userId);
  const initial = (name ?? userId)?.[0]?.toUpperCase() ?? "?";

  return (
    <div
      className={`${SIZE_MAP[size]} rounded-full flex items-center justify-center font-semibold flex-shrink-0 select-none ${className}`}
      style={{
        background: `hsl(${hue}, 55%, 90%)`,
        color: `hsl(${hue}, 65%, 35%)`,
      }}
      aria-label={name ?? `用户${userId.slice(0, 6)}`}
    >
      {initial}
    </div>
  );
}